import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appPath } from '@/api/client';
import { useRecordPost } from '@/api/RecordContext';
import { failureOf, said } from '@/api/errors';
import { useMeOrThrow } from '@/app/MeContext';
import { Select, Textarea } from '@/ui/fields';
import { Badge, Button, Field, Kicker, TextInput } from '@/ui/primitives';
import { useToast } from '@/ui/Toast';
import { localIsoDate } from '@/today/dates';

/**
 * Which supplier an offer can rest on, and whether there is demand
 * (proposal 0012, E4 and E5): the suppliers side by side on the fourteen
 * questions, the samples they sent and how they were judged, what each buyer
 * said it buys and would buy, and that added up per market.
 */

const P = {
  compare: appPath('/v1/suppliers/compare'),
  samples: appPath('/v1/suppliers/samples'),
  receive: appPath('/v1/suppliers/samples/receive'),
  judge: appPath('/v1/suppliers/samples/judge'),
  answers: appPath('/v1/buyers/answers'),
  addAnswer: appPath('/v1/buyers/answers/add'),
  scorecard: appPath('/v1/demand/scorecard'),
};

interface Comparison {
  suppliers: Array<{ id: string; name: string; country: string; role: 'current' | 'alternative'; priceListExpired: boolean; confirmed: number; samples: { accepted: number; rejected: number; pending: number } }>;
  rows: Array<{ key: string; label: string; cells: Array<{ supplierId: string; answer: string | null; confirmed: boolean; expired: boolean }> }>;
}

export function SupplierComparison() {
  const post = useRecordPost();
  const me = useMeOrThrow();
  const q = useQuery<Comparison>({ queryKey: ['supplier-compare', me.department], queryFn: () => post(P.compare, {}) });
  if (!q.data || q.data.suppliers.length < 1) return null;
  const { suppliers, rows } = q.data;
  return (
    <section className="flex flex-col gap-2" data-testid="supplier-compare">
      <Kicker>Side by side</Kicker>
      <div className="overflow-x-auto border border-line">
        <table className="w-full border-collapse text-dense">
          <thead>
            <tr>
              <th className="border-b border-line p-2 text-left text-meta">Question</th>
              {suppliers.map((s) => (
                <th key={s.id} className="border-b border-line p-2 text-left">
                  {s.name}
                  <span className="block text-micro text-meta">
                    {s.country} · {s.confirmed} in writing · samples {s.samples.accepted} good, {s.samples.rejected} not, {s.samples.pending} waiting
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="border-b border-line p-2 text-secondary">{r.label}</td>
                {r.cells.map((c) => (
                  <td key={c.supplierId} className="border-b border-line p-2">
                    {c.answer ?? <span className="text-meta">Not answered</span>}
                    {c.answer && !c.confirmed && <span className="block text-micro text-warning">{c.expired ? 'The price list it rests on has expired' : 'Said, not in writing'}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface Sample { id: string; product: string; format: string | null; quantity: string | null; receivedOn: string; verdict: 'pending' | 'accepted' | 'rejected'; verdictNote: string | null }

export function SupplierSamples({ supplierId }: { supplierId: string }) {
  const post = useRecordPost();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery<{ samples: Sample[] }>({ queryKey: ['supplier-samples', supplierId], queryFn: () => post(P.samples, { supplierId }) });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['supplier-samples', supplierId] });
    void qc.invalidateQueries({ queryKey: ['supplier-compare'] });
  };
  const failed = (e: unknown) => toast({ text: said(failureOf(e)), tone: 'danger' });
  const receive = useMutation({ mutationFn: (input: Record<string, string>) => post(P.receive, { supplierId, ...input }), onSuccess: done });
  const judge = useMutation({ mutationFn: (input: { sampleId: string; verdict: 'accepted' | 'rejected'; note: string }) => post(P.judge, input), onSuccess: done });
  const [product, setProduct] = useState('');
  const [format, setFormat] = useState('');
  const [quantity, setQuantity] = useState('');
  const [on, setOn] = useState(localIsoDate(new Date()));
  const [notes, setNotes] = useState<Record<string, string>>({});
  return (
    <section className="flex flex-col gap-3 border border-line p-4" data-testid="supplier-samples">
      <Kicker>Samples they sent</Kicker>
      {(q.data?.samples ?? []).map((s) => (
        <div key={s.id} className="flex flex-col gap-2 border-b border-line py-2">
          <span className="flex flex-wrap items-center gap-2 text-body">
            {s.product}
            {s.format ? ` · ${s.format}` : ''}
            {s.quantity ? ` · ${s.quantity}` : ''} · came {s.receivedOn}
            <Badge plain tone={s.verdict === 'accepted' ? 'accent' : s.verdict === 'rejected' ? 'danger' : 'warning'}>
              {s.verdict === 'accepted' ? 'Good enough' : s.verdict === 'rejected' ? 'Not good enough' : 'Waiting to be judged'}
            </Badge>
          </span>
          {s.verdictNote && <span className="text-dense text-secondary">{s.verdictNote}</span>}
          {s.verdict === 'pending' && (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="What you found">
                <Textarea aria-label={`What you found of ${s.product}`} rows={2} value={notes[s.id] ?? ''} onChange={(e) => setNotes({ ...notes, [s.id]: e.target.value })} />
              </Field>
              {(['accepted', 'rejected'] as const).map((verdict) => (
                <Button key={verdict} size="row" disabled={judge.isPending || !(notes[s.id] ?? '').trim()} onClick={() => judge.mutate({ sampleId: s.id, verdict, note: notes[s.id]! }, { onError: failed })}>
                  {verdict === 'accepted' ? 'Good enough' : 'Not good enough'}
                </Button>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Product">
          <TextInput aria-label="Sample product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Black garlic, peeled" />
        </Field>
        <Field label="Format">
          <TextInput aria-label="Sample format" value={format} onChange={(e) => setFormat(e.target.value)} placeholder="1 kg bag" />
        </Field>
        <Field label="How much">
          <TextInput aria-label="Sample quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </Field>
        <Field label="Came on">
          <TextInput type="date" aria-label="Sample came on" value={on} onChange={(e) => setOn(e.target.value)} />
        </Field>
        <Button
          disabled={!product.trim() || receive.isPending}
          onClick={() =>
            receive.mutate(
              { product, receivedOn: on, ...(format ? { format } : {}), ...(quantity ? { quantity } : {}) },
              {
                onSuccess: () => {
                  setProduct('');
                  toast({ text: 'Recorded. Judge it once you have tasted it.' });
                },
                onError: failed,
              },
            )
          }
        >
          A sample came
        </Button>
      </div>
    </section>
  );
}

interface Answer { field: string; label: string; answer: string; volumeKg: number | null; interest: 'yes' | 'no' | 'later' | null; source: string; recordedAt: string }

const FACTS = [
  { value: 'current_supplier', label: 'Buys it from now' },
  { value: 'volume', label: 'Volume a month' },
  { value: 'price_paid', label: 'Pays now' },
  { value: 'formats', label: 'Formats it wants' },
  { value: 'interest', label: 'Interested' },
  { value: 'decides', label: 'Who decides' },
  { value: 'next_step', label: 'What comes next' },
];

export function BuyerAnswers({ buyerId }: { buyerId: string }) {
  const post = useRecordPost();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery<{ answers: Answer[] }>({ queryKey: ['buyer-answers', buyerId], queryFn: () => post(P.answers, { buyerId }) });
  const add = useMutation({
    mutationFn: (input: Record<string, unknown>) => post(P.addAnswer, { buyerId, ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['buyer-answers', buyerId] });
      void qc.invalidateQueries({ queryKey: ['demand-scorecard'] });
    },
  });
  const [fact, setFact] = useState('interest');
  const [answer, setAnswer] = useState('');
  const [interest, setInterest] = useState<'yes' | 'no' | 'later'>('yes');
  const [kg, setKg] = useState('');
  const [source, setSource] = useState('call');
  return (
    <section className="flex flex-col gap-3 border border-line p-4" data-testid="buyer-answers">
      <Kicker>What they said</Kicker>
      {(q.data?.answers ?? []).length === 0 && <p className="m-0 text-dense text-meta">Nothing written down yet.</p>}
      {(q.data?.answers ?? []).map((a) => (
        <div key={a.field} className="flex flex-wrap gap-2 text-dense">
          <span className="text-secondary">{a.label}:</span>
          <span>{a.answer}</span>
          <span className="text-micro text-meta">({a.source})</span>
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="About">
          <Select aria-label="About" value={fact} onChange={(e) => setFact(e.target.value)} options={FACTS} />
        </Field>
        {fact === 'interest' && (
          <Field label="Interested">
            <Select aria-label="Interested" value={interest} onChange={(e) => setInterest(e.target.value as 'yes' | 'no' | 'later')} options={[{ value: 'yes', label: 'Yes' }, { value: 'later', label: 'Later' }, { value: 'no', label: 'No' }]} />
          </Field>
        )}
        {fact === 'volume' && (
          <Field label="kg a month">
            <TextInput type="number" aria-label="kg a month" min={0} value={kg} onChange={(e) => setKg(e.target.value)} />
          </Field>
        )}
        <Field label="What they said">
          <TextInput aria-label="What they said" value={answer} onChange={(e) => setAnswer(e.target.value)} />
        </Field>
        <Field label="How">
          <Select aria-label="How" value={source} onChange={(e) => setSource(e.target.value)} options={['call', 'email', 'meeting', 'fair', 'other'].map((v) => ({ value: v, label: v }))} />
        </Field>
        <Button
          disabled={!answer.trim() || add.isPending}
          onClick={() =>
            add.mutate(
              { field: fact, answer, source, ...(fact === 'interest' ? { interest } : {}), ...(fact === 'volume' && kg ? { volumeKg: Number(kg) } : {}) },
              { onSuccess: () => setAnswer(''), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
            )
          }
        >
          Write it down
        </Button>
      </div>
    </section>
  );
}

interface Score { marketCode: string; companyType: string; buyers: number; heardFrom: number; interested: number; notInterested: number; later: number; interestedVolumeKg: number }

export function DemandScorecard() {
  const post = useRecordPost();
  const me = useMeOrThrow();
  const q = useQuery<{ scores: Score[] }>({ queryKey: ['demand-scorecard', me.department], queryFn: () => post(P.scorecard, {}) });
  const scores = q.data?.scores ?? [];
  return (
    <section className="flex flex-col gap-2" data-testid="demand-scorecard">
      <Kicker>Is there demand?</Kicker>
      {scores.length === 0 && <p className="m-0 text-dense text-meta">No buyers yet.</p>}
      {scores.map((s) => (
        <div key={`${s.marketCode}-${s.companyType}`} className="flex flex-wrap gap-3 border-b border-line py-2 text-dense">
          <strong>{s.marketCode}</strong>
          <span>{s.companyType.replace(/_/g, ' ')}</span>
          <span>{s.heardFrom} of {s.buyers} heard from</span>
          <span>{s.interested} yes · {s.later} later · {s.notInterested} no</span>
          <span>{s.interestedVolumeKg > 0 ? `${s.interestedVolumeKg} kg a month among those who said yes` : 'no volume named yet'}</span>
        </div>
      ))}
    </section>
  );
}
